import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';

import { TransferableType, TransferObjectRef } from '../transfer.model';
import {
    PARTNER_FILTERABLE_TYPES,
    PARTNER_SCOPED_TYPES,
    TransferObjectSourceService,
    TransferPickItem
} from '../transfer-object-source.service';

@Component({
    selector: 'dmp-transfer-object-picker',
    templateUrl: './transfer-object-picker.component.html',
    styleUrls: ['./transfer-object-picker.component.scss'],
    standalone: false
})
export class TransferObjectPickerComponent implements OnChanges {
    @Input() type: TransferableType;
    @Input() sourceTenant: string;
    /** Codes already in the cart for this type, so they can be shown as disabled/checked. */
    @Input() selectedCodes: string[] = [];
    @Output() add = new EventEmitter<TransferObjectRef[]>();

    items: TransferPickItem[] = [];
    selection: TransferPickItem[] = [];
    filterText = '';
    partner: string;
    loading = false;

    constructor(
        private source: TransferObjectSourceService,
        private messageService: MessageService,
        private readonly translate: TranslateService
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.sourceTenant || changes.type) {
            this.partner = null;
            this.items = [];
            this.selection = [];
            this.load();
        }
    }

    get requiresPartner(): boolean {
        return PARTNER_SCOPED_TYPES.has(this.type);
    }

    get supportsPartnerFilter(): boolean {
        return PARTNER_FILTERABLE_TYPES.has(this.type);
    }

    onPartnerChange(partner: string): void {
        this.partner = partner;
        this.load();
    }

    load(): void {
        if (!this.sourceTenant || !this.type || (this.requiresPartner && !this.partner)) {
            this.items = [];
            return;
        }
        this.loading = true;
        this.source.listObjects(this.type, this.sourceTenant, this.partner).subscribe({
            next: items => {
                this.items = items;
                this.loading = false;
            },
            error: () => {
                this.loading = false;
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('components.dyn-raw-detail-display.errorSummary'),
                    detail: this.translate.instant('tenantTransfer.picker.loadErrorDetail')
                });
            }
        });
    }

    isAlreadyInCart(item: TransferPickItem): boolean {
        return this.selectedCodes.includes(item.code);
    }

    addSelectionToCart(): void {
        const refs = this.selection
            .filter(item => !this.isAlreadyInCart(item))
            .map(item => ({ type: this.type, code: item.code, name: item.name }));
        if (refs.length > 0) {
            this.add.emit(refs);
        }
        this.selection = [];
    }
}
